import { getTimeZones } from '@vvo/tzdb';
import Case from 'case';
import {
  format,
  formatDistance,
  getHours,
  getYear,
  isToday,
  isTomorrow,
  isYesterday,
  millisecondsToMinutes,
  startOfDay,
} from 'date-fns';

import {
  HOURS_IN_AM_PM,
  HOURS_IN_DAY,
  MINUTES_IN_HOUR,
  ONE,
  SECONDS_IN_MINUTE,
  TWO,
  ZERO,
} from '@/constants';

import type { CoachAssignment, User } from '@/types';
import type { TimeZone } from '@vvo/tzdb';

// TODO: move to types
export enum DATE_FORMATS {
  DAY_HOUR_AM_PM = 'EEEE, h:mmaaa',
  DAY_MONTH_NUMBER = 'EEEE, MMMM do',
  HOUR_MINUTE = 'h:mm',
  HOUR_MINUTE_AM_PM = 'h:mmaaa',
}

export const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const findTimezone = (tz: string) =>
  getTimeZones().find(({ name, group }) => tz === name || group.includes(tz));

const timeZoneNamePart = (
  tz: string,
  at: Date,
  timeZoneName: 'longOffset' | 'short',
) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value ?? '';

// MEN-5421: tzdb's abbreviation is the zone's standard-time name, so a summer
// New York time read "EST". On a daylight-time date, use Intl's name for that
// date instead (EDT, PDT, GMT+1).
const abbreviationAt = (
  tz: string,
  { abbreviation, rawOffsetInMinutes }: TimeZone,
  at: Date,
) => {
  try {
    const [, sign, hours, minutes] =
      /GMT([+-])(\d{2}):(\d{2})/u.exec(
        timeZoneNamePart(tz, at, 'longOffset'),
      ) ?? [];
    const offset = sign
      ? (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
      : 0;
    return offset === rawOffsetInMinutes
      ? abbreviation
      : timeZoneNamePart(tz, at, 'short') || abbreviation;
  } catch {
    return abbreviation;
  }
};

export const getGMTTimeZone = (
  tz: string,
  abreviated?: boolean,
  at: Date = new Date(),
) => {
  const data = findTimezone(tz);

  if (data) {
    const abbreviation = abbreviationAt(tz, data, at);
    if (abreviated) {
      return abbreviation;
    }
    const { alternativeName } = data;
    if (alternativeName) {
      const acronym = alternativeName
        .split(/[^A-Za-z]+/u)
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

      const shortLabel = (abbreviation || acronym).trim();

      if (shortLabel && shortLabel !== alternativeName) {
        return `(${shortLabel})`;
      }
      return `(${alternativeName})`;
    }
  }

  return '';
};

// April 22
export const toMonthString = (date: Date) => {
  if (!isValidDate(date)) return '';
  const locale = 'en';

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });
};

export const formatMonthDayYear = (value: Date | string | number) => {
  const date = new Date(value);
  if (!isValidDate(date)) return '';

  return format(date, 'MMM d, yyyy');
};

// Tuesday, April 22 (GMT-7:00)
export const toDateString = (date: Date, timeZone?: string) => {
  if (!isValidDate(date)) return '';
  const gmtTimeZone = timeZone ? getGMTTimeZone(timeZone, false, date) : '';
  const locale = 'en';

  const dateString = date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    timeZone,
    weekday: 'long',
  });

  return `${dateString} ${gmtTimeZone}`;
};

// 01-01-2023
export const formatDateDDMMYYYY = (date: Date) => {
  if (!isValidDate(date)) return '';
  const toLocaleDate = date.toLocaleDateString('en', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const dashedDate = toLocaleDate.replaceAll('/', '-');
  return dashedDate;
};

// Tuesday, April 22 1:05 pm (GMT-7:00)
export const toDateTimeString = (
  value: Date | string,
  timeZone?: string,
  month: 'long' | 'short' = 'long',
  weekday: 'long' | 'short' = 'long',
) => {
  const date = new Date(value);
  if (!isValidDate(date)) return '';
  let gmtTimeZone = '';

  if (timeZone) {
    gmtTimeZone = getGMTTimeZone(timeZone, false, date);
  }

  const locale = 'en';

  const dateString = date.toLocaleDateString(locale, {
    day: 'numeric',
    month,
    timeZone,
    weekday,
  });
  const timeString = date.toLocaleTimeString(locale, {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  });

  return `${dateString} ${timeString} ${gmtTimeZone}`;
};

// April 22 1:05 pm-2:05pm (GMT-7:00) or 1:05 pm-2:05pm (GMT-7:00)
export const toDateTimeRangeString = (
  start: Date,
  end: Date,
  timeZone: string,
  skipDay?: boolean,
) => {
  const gmtTimeZone = getGMTTimeZone(timeZone, true, start);
  const locale = 'en';

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    timeZone,
  };
  const dateString = start.toLocaleDateString(locale, dateOptions);
  const startTimeString = start.toLocaleTimeString(locale, {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  });

  const endTimeString = end.toLocaleTimeString(locale, {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone,
  });

  if (skipDay) return `${startTimeString}-${endTimeString} ${gmtTimeZone}`;

  return `${dateString} ${startTimeString}-${endTimeString} ${gmtTimeZone}`;
};

const shortDistanceFrom = (date: Date, includeMinutes = false) => {
  const today = isToday(date);
  const tomorrow = isTomorrow(date);

  const minutesFormat = includeMinutes ? " 'at' h:mm aaa" : "'";
  let dateText = '';
  if (today) {
    dateText = format(date, `'Today'${minutesFormat}`);
  } else if (tomorrow) {
    dateText = format(date, `'Tomorrow'${minutesFormat}`);
  } else {
    dateText = format(date, `M/dd/yy${minutesFormat}`);
  }

  return dateText;
};

const distanceFrom = (date: Date, includeMinutes = false) => {
  const distance = formatDistance(
    date,
    !includeMinutes ? startOfDay(new Date()) : new Date(),
    {
      addSuffix: false,
    },
  );

  if (
    !includeMinutes &&
    (distance === 'less than a minute' || distance === '1 minute')
  ) {
    return 'Just now';
  }
  if (distance === '1 day') {
    return 'Yesterday';
  }
  if (
    !includeMinutes &&
    (distance.endsWith('minute') ||
      distance.endsWith('minutes') ||
      distance.endsWith('hour') ||
      distance.endsWith('hours'))
  ) {
    return 'Today';
  }
  return `${Case.sentence(distance)} ago`;
};

const formatDay = (
  day: { end: string; start: string },
  currentMonth: number,
  selectedDay: number,
) => {
  const now = new Date().getTime();
  const currentYear = getYear(now);
  const startHour = parseInt(day.start.split(':')[0] ?? '', 10);
  const startMinute = parseInt(
    day.start.split(':')[1]?.slice(ZERO, TWO) ?? '',
    10,
  );
  const endHour = parseInt(day.end.split(':')[0] ?? '', 10);
  const endMinute = parseInt(day.end.split(':')[1]?.slice(ZERO, TWO) ?? '', 10);
  const isPMStart = day.start.includes('PM');
  const isPMEnd = day.end.includes('PM');
  return {
    end: new Date(
      currentYear,
      currentMonth,
      selectedDay,
      isPMEnd ? endHour + HOURS_IN_AM_PM : endHour,
      endMinute,
    ),
    start: new Date(
      currentYear,
      currentMonth,
      selectedDay,
      isPMStart ? startHour + HOURS_IN_AM_PM : startHour,
      startMinute,
    ),
  };
};

const generateTimes = () => {
  const x = 15;
  const times = [];
  let tt = ZERO;
  const ap = ['AM', 'PM'];
  for (let i = ZERO; tt < HOURS_IN_DAY * MINUTES_IN_HOUR; i += ONE) {
    const hh = Math.floor(tt / MINUTES_IN_HOUR);
    const mm = tt % SECONDS_IN_MINUTE;
    const hour = `${`0${
      hh === HOURS_IN_AM_PM ? HOURS_IN_AM_PM : hh % HOURS_IN_AM_PM
    }`.slice(-2)}:${`0${mm}`.slice(-2)}${ap[Math.floor(hh / HOURS_IN_AM_PM)]}`;
    times[i] = { label: hour, value: hour };
    tt += x;
  }
  return times;
};

// format minutes to be two digits
const formatDate = (date: Date) => {
  const isPM = getHours(date) >= HOURS_IN_AM_PM;
  const is12 = getHours(date) === HOURS_IN_AM_PM;
  const hour = is12 ? HOURS_IN_AM_PM : getHours(date) % HOURS_IN_AM_PM;
  return `${hour}:${date.getMinutes() === ZERO ? '00' : date.getMinutes()} ${
    isPM ? 'PM' : 'AM'
  }`;
};
const returnMinuteRange = (start: number, end: number) =>
  parseInt(
    (millisecondsToMinutes(end - start) / MINUTES_IN_HOUR)
      .toFixed(TWO)
      .toString()
      .split('.')[1] ?? '',
    10,
  ) >= 75
    ? Math.ceil(millisecondsToMinutes(end - start) / SECONDS_IN_MINUTE)
    : Math.floor(
        millisecondsToMinutes(Math.floor((end - start) / SECONDS_IN_MINUTE)),
      );

const formatToday = (
  date: string | Date | undefined,
  config?: {
    format?: string;
    todayFormat?: string;
    tomorrowFormat?: string;
    yesterdayFormat?: string;
  },
) => {
  const {
    todayFormat = "'Today at' h:mm aaa",
    yesterdayFormat = "'Yesterday at' h:mm aaa",
    tomorrowFormat = "'Tomorrow at' h:mm aaa",
    format: customFormat = "M/dd/yy 'at' h:mm aaa",
  } = config || {};

  const d = date ? new Date(date) : undefined;

  if (!d) {
    return '';
  }

  if (isToday(d)) {
    return format(d, todayFormat);
  }

  if (isYesterday(d)) {
    return format(d, yesterdayFormat);
  }

  if (isTomorrow(d)) {
    return format(d, tomorrowFormat);
  }

  return format(d, customFormat);
};

// Rename to isToday once we get rid of date-fns
const isDateToday = (date?: Date | string) => {
  if (!date) return false;
  return (
    new Intl.DateTimeFormat('en-US').format(new Date(date)) ===
    new Intl.DateTimeFormat('en-US').format(new Date())
  );
};

// 'Fri, Dec 22'
const formatShortDayMonthDate = (date: Date | string) =>
  new Date(date).toLocaleDateString('en', {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });

const formatMonthAndYear = (date: Date | string) =>
  new Date(date).toLocaleDateString('en', {
    month: 'long',
    year: 'numeric',
  });

// Earliest coach assignment is when this coach and member were matched.
const getMatchedInLabel = (coach: User, relationships: CoachAssignment[]) => {
  const matchedAt = relationships
    .filter((r) => r.coachId === coach.id)
    .map(({ createdAt }) => new Date(createdAt).getTime());

  if (!matchedAt.length) return '';

  return `Matched in ${formatMonthAndYear(new Date(Math.min(...matchedAt)))}`;
};

// TODO: simply type when when we migrate session data to our new backend and get
// consistent types
const isInNumberOfDays = (date: string | Date, numberOfDays: number) => {
  const today = new Date();

  const daysFromToday = new Date(today);
  daysFromToday.setDate(today.getDate() + numberOfDays);

  return new Date(date) < daysFromToday;
};

export {
  shortDistanceFrom,
  distanceFrom,
  formatDay,
  formatDate,
  returnMinuteRange,
  generateTimes,
  formatToday,
  isDateToday,
  formatMonthAndYear,
  formatShortDayMonthDate,
  getMatchedInLabel,
  isInNumberOfDays,
};
